import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Steps, Flex, Button } from 'antd';
import { useTripTaskStore } from 'shared/stores';
import type { ETripTaskType } from 'types/trips';
import { TaskDetails } from '../trip-create-task-modal/components/task-details/task-details';
import { EditTaskType } from './components/edit-task-type';
import { LOADING_TYPES } from './constants';
import { type ITripEditTaskModalProps, EStepKey } from './types';
import { validateEditDetailsStep, buildUpdateTripTaskPayload } from './utils';

export const TripEditTaskModal = (props: ITripEditTaskModalProps) => {
  const { open, onCancel, tripTask, tripStatus, onSubmit, isPending } = props;
  const { t } = useTranslation('trip');
  const [currentIndex, setCurrentIndex] = useState(0);
  const storeState = useTripTaskStore();
  const { taskType, reset, initializeForEdit, setCargoTrips } = storeState;

  const steps = [
    { title: t('create_tasks_step_task_type'), key: EStepKey.taskType },
    { title: t('create_tasks_step_details'), key: EStepKey.details },
  ];

  const currentStepKey = steps[currentIndex]?.key;
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;
  const isDetailsStepSkipped = tripStatus !== 'planning' && LOADING_TYPES.includes(tripTask.type as ETripTaskType);

  const validateFormStep = () => {
    switch (currentStepKey) {
      case EStepKey.taskType:
        return Boolean(taskType);
      case EStepKey.details:
        return validateEditDetailsStep(storeState);
      default:
        return true;
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => prev - 1);
  };

  const handleFinish = () => {
    const payload = buildUpdateTripTaskPayload(storeState);
    onSubmit(payload);
  };

  useEffect(() => {
    if (open) {
      reset();
      initializeForEdit(tripTask);
      setCargoTrips([]);
    }
  }, [open, tripTask, reset, initializeForEdit, setCargoTrips]);

  useEffect(() => {
    if (open) {
      if (isDetailsStepSkipped) {
        setCurrentIndex(1);
      } else {
        setCurrentIndex(0);
      }
    }
  }, [open, isDetailsStepSkipped]);

  const renderStepContent = (stepKey: EStepKey) => {
    switch (stepKey) {
      case EStepKey.taskType:
        return <EditTaskType tripStatus={tripStatus} />;
      case EStepKey.details:
        return <TaskDetails />;
      default:
        return null;
    }
  };

  const currentStepComponent = currentStepKey ? renderStepContent(currentStepKey) : null;
  const isStepValid = validateFormStep();

  return (
    <Modal
      open={open}
      title={t('edit_task_modal_title')}
      width={720}
      footer={
        <Flex justify='space-between'>
          <Button onClick={onCancel}>{t('common:cancel')}</Button>
          <Flex gap='small'>
            <Button disabled={isFirstStep || isDetailsStepSkipped} onClick={handlePrev}>
              {t('common:back')}
            </Button>
            {isLastStep ? (
              <Button type='primary' disabled={!isStepValid || isPending} onClick={handleFinish}>
                {t('common:save')}
              </Button>
            ) : (
              <Button type='primary' disabled={!isStepValid} onClick={handleNext}>
                {t('common:next')}
              </Button>
            )}
          </Flex>
        </Flex>
      }
      onCancel={onCancel}>
      <Flex gap='large' vertical>
        <Steps current={currentIndex} items={steps} />
        {currentStepComponent}
      </Flex>
    </Modal>
  );
};
